import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAppConfig } from "@/hooks/useAppConfig";
import { 
  subscribeToProducts, 
  addProduct, 
  updateProduct, 
  deleteProduct,
  ProductData 
} from "@/services/productService";
import { useAuth } from "@/contexts/AuthContext";
import { exportProducts, generateProductTemplate } from "@/services/excelExportService";
import { Package, Plus, Edit2, Trash2, Search, Download, MoreVertical, TrendingUp, Calendar } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

interface ProductManagementProps {
  hideDelete?: boolean;
}

export function ProductManagement({ hideDelete = false }: ProductManagementProps) {
  const { config } = useAppConfig();
  const { user } = useAuth();
  const [products, setProducts] = useState<ProductData[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<ProductData | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    bank: "",
    category: "system" as "personal" | "system",
    minCibil: "",
    minSalary: "",
    roiRange: "",
    processingFee: "",
    maxTenure: "",
    productDetails: "",
    salesManagerNotes: "",
    customerNotes: "",
  });

  // Subscribe to products
  useEffect(() => {
    const unsubscribe = subscribeToProducts(
      (productsData) => {
        setProducts(productsData);
        setLoading(false);
      },
      (error) => {
        toast.error("Failed to load products");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.bank.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const confirmDelete = (product: ProductData) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!productToDelete?.id) return;
    
    try {
      await deleteProduct(productToDelete.id);
      toast.success("Product deleted successfully");
    } catch (error) {
      toast.error("Failed to delete product");
    } finally {
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const handleExport = () => {
    if (filteredProducts.length === 0) {
      toast.error("No products to export");
      return;
    }
    try {
      exportProducts(filteredProducts);
      toast.success(`Exported ${filteredProducts.length} products to Excel`);
    } catch (error) {
      toast.error("Failed to export products");
      console.error("Export error:", error);
    }
  };

  const handleAddNew = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      bank: "",
      category: "system",
      minCibil: "",
      minSalary: "",
      roiRange: "",
      processingFee: "",
      maxTenure: "",
      productDetails: "",
      salesManagerNotes: "",
      customerNotes: "",
    });
    setSheetOpen(true);
  };

  const handleEdit = (product: ProductData) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      bank: product.bank,
      category: product.category,
      minCibil: product.minCibil?.toString() || "",
      minSalary: product.minSalary?.toString() || "",
      roiRange: product.roiRange || "",
      processingFee: product.processingFee || "",
      maxTenure: product.maxTenure || "",
      productDetails: product.productDetails || "",
      salesManagerNotes: product.salesManagerNotes || "",
      customerNotes: product.customerNotes || "",
    });
    setSheetOpen(true);
  };

  const handleSaveProduct = async () => {
    if (!formData.name.trim() || !formData.bank.trim()) {
      toast.error("Name and bank are required");
      return;
    }

    try {
      const productData: Omit<ProductData, 'id'> = {
        name: formData.name,
        bank: formData.bank,
        category: formData.category,
        minCibil: formData.minCibil ? parseInt(formData.minCibil) : 0,
        minSalary: formData.minSalary ? parseInt(formData.minSalary) : 0,
        roiRange: formData.roiRange || "",
        processingFee: formData.processingFee || "",
        maxTenure: formData.maxTenure || "",
        productDetails: formData.productDetails || "",
        salesManagerNotes: formData.salesManagerNotes || "",
        customerNotes: formData.customerNotes || "",
        createdBy: user?.phone || "",
      };

      if (editingProduct?.id) {
        await updateProduct(editingProduct.id, productData);
        toast.success("Product updated successfully");
      } else {
        await addProduct(productData);
        toast.success("Product added successfully");
      }
      setSheetOpen(false);
      setEditingProduct(null);
    } catch (error) {
      toast.error(editingProduct ? "Failed to update product" : "Failed to add product");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Product Management
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button size="sm" onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Product List */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-muted/50 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No products found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block md:hidden space-y-2">
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border rounded-lg p-3 active:bg-muted/50 tap-highlight-green"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Avatar className="h-9 w-9 flex-shrink-0">
                          <AvatarFallback className="bg-purple-500 text-white text-xs font-semibold">
                            {product.name.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{product.name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Badge variant="outline" className="text-[9px] px-1 py-0">
                              {product.bank}
                            </Badge>
                            <Badge 
                              variant={product.category === "system" ? "default" : "secondary"} 
                              className="text-[9px] px-1 py-0"
                            >
                              {product.category}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-9 w-9">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem onClick={() => handleEdit(product)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {!hideDelete && (
                              <DropdownMenuItem 
                                onClick={() => confirmDelete(product)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs pt-2 border-t">
                      {product.minCibil && (
                        <div>
                          <p className="text-muted-foreground mb-0.5">CIBIL</p>
                          <p className="font-medium">{product.minCibil}+</p>
                        </div>
                      )}
                      {product.roiRange && (
                        <div>
                          <p className="text-muted-foreground mb-0.5 flex items-center gap-0.5">
                            <TrendingUp className="h-3 w-3" />
                            ROI
                          </p>
                          <p className="font-medium truncate">{product.roiRange}</p>
                        </div>
                      )}
                      {product.maxTenure && (
                        <div>
                          <p className="text-muted-foreground mb-0.5 flex items-center gap-0.5">
                            <Calendar className="h-3 w-3" />
                            Tenure
                          </p>
                          <p className="font-medium truncate">{product.maxTenure}</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Desktop List View */}
              <div className="hidden md:block space-y-2">
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{product.name}</p>
                        <Badge variant="outline" className="text-[10px]">
                          {product.bank}
                        </Badge>
                        <Badge variant={product.category === "system" ? "default" : "secondary"} className="text-[10px]">
                          {product.category}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        {product.minCibil && <span>CIBIL: {product.minCibil}+</span>}
                        {product.roiRange && (
                          <>
                            <span>•</span>
                            <span>ROI: {product.roiRange}</span>
                          </>
                        )}
                        {product.maxTenure && (
                          <>
                            <span>•</span>
                            <span>{product.maxTenure}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEdit(product)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      {!hideDelete && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => confirmDelete(product)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Add/Edit Product Sheet */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl overflow-y-auto">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-xl font-bold">
                {editingProduct ? "Edit Product" : "Add New Product"}
              </SheetTitle>
            </SheetHeader>
            
            <div className="space-y-5 pb-8">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Product Name *</Label>
                <Select 
                  value={formData.name} 
                  onValueChange={(v) => setFormData(p => ({ ...p, name: v }))}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {config.products.map((product) => (
                      <SelectItem key={product} value={product}>{product}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Bank *</Label>
                <Select 
                  value={formData.bank} 
                  onValueChange={(v) => setFormData(p => ({ ...p, bank: v }))}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {config.banks.map((bank) => (
                      <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData(p => ({ ...p, category: v as "personal" | "system" }))}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">System</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Min CIBIL</Label>
                  <Input 
                    value={formData.minCibil} 
                    onChange={(e) => setFormData(p => ({ ...p, minCibil: e.target.value }))}
                    placeholder="e.g., 720"
                    type="number"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Min Salary</Label>
                  <Input 
                    value={formData.minSalary} 
                    onChange={(e) => setFormData(p => ({ ...p, minSalary: e.target.value }))}
                    placeholder="e.g., 35000"
                    type="number"
                    className="h-12"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">ROI Range</Label>
                <Input 
                  value={formData.roiRange} 
                  onChange={(e) => setFormData(p => ({ ...p, roiRange: e.target.value }))}
                  placeholder="e.g., 8.5% - 9.15%"
                  className="h-12"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Processing Fee</Label>
                  <Input 
                    value={formData.processingFee} 
                    onChange={(e) => setFormData(p => ({ ...p, processingFee: e.target.value }))}
                    placeholder="e.g., ₹3,000 + GST"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Max Tenure</Label>
                  <Input 
                    value={formData.maxTenure} 
                    onChange={(e) => setFormData(p => ({ ...p, maxTenure: e.target.value }))}
                    placeholder="e.g., 30 years"
                    className="h-12"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Product Details</Label>
                <Textarea 
                  value={formData.productDetails} 
                  onChange={(e) => setFormData(p => ({ ...p, productDetails: e.target.value }))}
                  placeholder="Enter detailed product information, features, benefits, etc..."
                  className="min-h-24"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Sales Manager Notes</Label>
                <Textarea 
                  value={formData.salesManagerNotes} 
                  onChange={(e) => setFormData(p => ({ ...p, salesManagerNotes: e.target.value }))}
                  placeholder="Internal notes for sales team..."
                  className="min-h-20"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Customer Notes</Label>
                <Textarea 
                  value={formData.customerNotes} 
                  onChange={(e) => setFormData(p => ({ ...p, customerNotes: e.target.value }))}
                  placeholder="Customer-facing notes..."
                  className="min-h-20"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1 h-12" 
                  onClick={() => setSheetOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 h-12" 
                  onClick={handleSaveProduct}
                >
                  {editingProduct ? "Update" : "Add"} Product
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Product</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete {productToDelete?.name}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
